import React, {useCallback, useMemo, useState} from 'react';
import {
    ReactZoomPanPinchRef,
    TransformComponent,
    TransformWrapper,
    useControls,
    useTransformContext,
} from 'react-zoom-pan-pinch';
import {
    colors,
    ImageViewerToolbar,
    ImageViewerWrapper,
    NoImageInfo,
    NoImageViewer,
    StyledIconButton, StyledImageView,
    StyledToolButton,
    StyledZoomInfo,
    StyledZoomSlider, StyledZoomSliderElement,
    StyledZoomSliderWrapper
} from "./ImageViewer.styled";
import {formatfileDataToDataUrl} from "../../../utils/ocr-utils";
import {useTranslation} from "react-i18next";

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 30;
const LOG_MIN = Math.log(MIN_ZOOM);
const LOG_MAX = Math.log(MAX_ZOOM);

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const zoomToSlider = (zoom: number) => {
    return ((Math.log(clamp(zoom, MIN_ZOOM, MAX_ZOOM)) - LOG_MIN) /
        (LOG_MAX - LOG_MIN)) * 100;
}

const sliderToZoom = (pct: number) => {
    return clamp(Math.exp(LOG_MIN + (pct / 100) * (LOG_MAX - LOG_MIN)), MIN_ZOOM, MAX_ZOOM);
}

const Toolbar = ({ zoom }: { zoom: number }) => {
    const {t} = useTranslation()
    const { zoomIn, zoomOut, resetTransform, setTransform } = useControls();
    const context = useTransformContext();

    const handleSlider = (newZoom: number) => {
        const positionX = context?.state?.positionX ?? 0;
        const positionY = context?.state?.positionY ?? 0;

        setTransform(
            positionX,
            positionY,
            newZoom,
            200,
            'easeOut'
        );
    };

    return (
        <ImageViewerToolbar>
            <StyledIconButton type="button" aria-label={t('ocr.button.zoomOut')} onClick={() => zoomOut(0.1)}>
                -
            </StyledIconButton>

            <StyledZoomSliderWrapper>
                <StyledZoomSlider $width={`${zoomToSlider(zoom)}%`} />
                <StyledZoomSliderElement
                    type="range"
                    min="0"
                    max="100"
                    step="0.0001"
                    value={zoomToSlider(zoom)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleSlider(sliderToZoom(parseFloat(e.target.value)))
                    }
                    aria-label={t('ocr.aria.zoom')}
                />
            </StyledZoomSliderWrapper>

            <StyledIconButton type="button" aria-label={t('ocr.button.zoomIn')} onClick={() => zoomIn(0.1)}>
                +
            </StyledIconButton>

            <StyledZoomInfo>
                {`${Math.round(zoom * 100)}%`}
            </StyledZoomInfo>

            <StyledToolButton type="button" onClick={() => resetTransform()}>
                {t('ocr.button.backToOriginalZoom')}
            </StyledToolButton>
        </ImageViewerToolbar>
    );
}

interface ImageViewerProps {
    mimeType: string;
    src?: string;
    data?: Uint8Array | Int8Array | ArrayBuffer | number[];
}

export const ImageViewer = ({ src, data, mimeType }: ImageViewerProps) => {
    const {t} = useTranslation();

    const dataUrl = useMemo(
        () => formatfileDataToDataUrl({src, data, mimeType}),
        [src, data, mimeType],
    );
    const [zoom, setZoom] = useState<number>(1);

    const onTransform = useCallback(
        (_ref: ReactZoomPanPinchRef, state: { scale: number; positionX: number; positionY: number; }) => {
            setZoom(state.scale);
        }, []
    );

    return (
        <ImageViewerWrapper>
            {!dataUrl ? (
                <NoImageViewer>
                    <NoImageInfo>
                        {t('ocr.imageViewer.noImage')}
                    </NoImageInfo>
                </NoImageViewer>
            ) : (
                <TransformWrapper
                    initialScale={1}
                    minScale={MIN_ZOOM}
                    maxScale={MAX_ZOOM}
                    centerOnInit
                    onTransform={onTransform}
                    wheel={{ step: 0.0005 }}
                    pinch={{ step: 5 }}
                    doubleClick={{ step: 0.7 }}
                >
                    <Toolbar zoom={zoom} />

                    <TransformComponent
                        wrapperStyle={{ flex: 1, width: '100%', background: colors.bg, cursor: 'grab' }}
                        contentStyle={{ cursor: 'inherit' }}
                    >
                        <StyledImageView
                            src={dataUrl}
                            alt={t('ocr.imageViewer.imageAlt')}
                            draggable={false}
                            $zoom={zoom}
                        />
                    </TransformComponent>
                </TransformWrapper>
            )}
        </ImageViewerWrapper>
    );
}

export default ImageViewer;